import { useCallback } from 'react';
import { CatalogCategoryType } from '..';
import { PagesForTelemetry } from '../Catalog.telemetry.types';
import { MinimalCatalogCategoryWithCsp } from '../Catalog.types';
import { useCatalogSearchBarContext } from '../components/CatalogSearchBar';
import { tryGetStoredCurationForProduct } from '../hooks/useStoredCuration';
import { CurationState } from '../state/Catalog';

const CATEGORY_TYPE_BY_LINEAGE_COUNT = {
  0: 'root',
  1: 'department',
  2: 'category',
  3: 'subcategory',
} as const;

const CURATION_CATEGORY_BY_LINEAGE_COUNT = {
  0: 'root',
  1: 'curationRoot',
  2: 'curation',
} as const;

type CurrationCategoryKeys = keyof typeof CURATION_CATEGORY_BY_LINEAGE_COUNT;
export type DeriveTelemetryPageViewType = 'productGrid' | 'pdp' | 'pdpModal' | 'searchBar';

export type DeriveTelemetryPageArgs = {
  defaultValue?: PagesForTelemetry;
  view: DeriveTelemetryPageViewType;
  product: Maybe<{ id: string }>;
  selectedCategory: Maybe<MinimalCatalogCategoryWithCsp>;
};

export type DeriveTelemetryPageFn = typeof deriveTelemetryPage;

export const deriveTelemetryPage = (args: DeriveTelemetryPageArgs): PagesForTelemetry => {
  const { selectedCategory, defaultValue, product, view } = args;

  const curationContainingProduct = tryGetStoredCurationForProduct(product);

  // Product details can be rendered in two different views:
  // - in a page
  // - in a modal
  if (view === 'pdp' || view === 'pdpModal') {
    return 'productDetail';
  }

  if (view === 'productGrid' || view === 'searchBar') {
    if (curationContainingProduct) {
      return 'curationDetail';
    }

    if (selectedCategory) {
      const maybeLineage = (selectedCategory && (selectedCategory.lineageCsp || selectedCategory.lineage)) || [];

      const maybeCategoryType = CATEGORY_TYPE_BY_LINEAGE_COUNT[maybeLineage.length];
      if (maybeCategoryType) {
        return maybeCategoryType;
      }
    }
  }

  return defaultValue || (curationContainingProduct ? 'curationRoot' : 'root');
};

/**
 * The returned utility method will factor in the current catalog search query, if any,
 * when determining the telemetry page.
 *
 * This hook is intended to fully replace the usage of `deriveTelemetryPage`
 */
export const useDeriveTelemetryPage = () => {
  const { searchQuery } = useCatalogSearchBarContext();

  return useCallback<typeof deriveTelemetryPage>(
    args => {
      return searchQuery ? 'searchResults' : deriveTelemetryPage(args);
    },
    [searchQuery],
  );
};

export const deriveProductCatalogCategoryType = (args: {
  defaultValue?: CatalogCategoryType;
  product: Maybe<{ id: string }>;
  selectedCategory: Maybe<MinimalCatalogCategoryWithCsp>;
  curation?: Maybe<CurationState>;
}): CatalogCategoryType => {
  const { curation, selectedCategory, defaultValue = 'root', product } = args;
  const storedCuration = curation || tryGetStoredCurationForProduct(product);

  const maybeLineage = (selectedCategory && (selectedCategory.lineageCsp || selectedCategory.lineage)) || [];

  if (selectedCategory?.isCurationCategory) {
    return CURATION_CATEGORY_BY_LINEAGE_COUNT[maybeLineage.length as CurrationCategoryKeys];
  }

  return (storedCuration ? 'curation' : maybeLineage && CATEGORY_TYPE_BY_LINEAGE_COUNT[maybeLineage.length]) || defaultValue;
};
